import { useState, KeyboardEvent, ChangeEvent } from 'react'
import { X } from 'lucide-react'

interface FlexibleTagInputProps {
  tags: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  maxTags?: number
}

export default function FlexibleTagInput({
  tags,
  onChange,
  placeholder = 'Enter tags...',
  maxTags = 10,
}: FlexibleTagInputProps) {
  const [inputValue, setInputValue] = useState('')

  // Parse input and extract tags
  const parseAndAddTags = (input: string) => {
    // Split by comma, semicolon, or multiple spaces
    const delimiters = /[,;\s]+/
    const newTags = input
      .split(delimiters)
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0 && tag.length <= 50) // Valid tags only
      .filter(tag => !tags.includes(tag)) // No duplicates

    if (newTags.length > 0) {
      const combinedTags = [...tags, ...newTags].slice(0, maxTags)
      onChange(combinedTags)
    }

    setInputValue('')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault()
      if (inputValue.trim()) {
        parseAndAddTags(inputValue)
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace when input is empty
      onChange(tags.slice(0, -1))
    }
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    // Check for delimiters at the end
    if (value.endsWith(',') || value.endsWith(';') || value.endsWith('  ')) {
      parseAndAddTags(value)
    } else {
      setInputValue(value)
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      parseAndAddTags(inputValue)
    }
  }

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter(tag => tag !== tagToRemove))
  }

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-gray-200 bg-white min-h-[48px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
        {tags.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {tags.length < maxTags && (
          <input
            type="text"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={tags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] outline-none text-gray-800 placeholder-gray-400"
          />
        )}
      </div>
      {tags.length >= maxTags && (
        <p className="mt-1 text-xs text-orange-600">
          Maximum {maxTags} tags reached
        </p>
      )}
    </div>
  )
}
